import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

export interface PhotoFile {
  file: File;
  dataUrl: string; // Base64 data URL for preview
  id: string; // Unique identifier for the photo
}

/**
 * Compresses an image file using canvas
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed file
 */
async function compressImageFile(
  file: File,
  {
    maxWidth = 1280,
    maxHeight = 1280,
    quality = 0.7, // 0–1
  }: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
  } = {}
): Promise<File> {
  if (!file.type.startsWith('image/')) return file; // skip non-images

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      try {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = width * ratio;
        height = height * ratio;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src);
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const compressedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
              type: 'image/jpeg',
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
}

/**
 * Converts a Capacitor URI/webPath to a File object
 * @param uri - The URI or webPath from Camera.getPhoto
 * @param fileName - The desired file name
 * @returns Promise<File> - The File object
 */
async function fileFromUri(uri: string, fileName: string): Promise<File> {
  try {
    // Fetch the file from the URI
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error(`Failed to fetch image from URI: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  } catch (error) {
    console.error('Error converting URI to File:', error);
    throw error;
  }
}

/**
 * Takes a photo using the device camera
 * @returns Promise<PhotoFile | null> - The captured photo or null if cancelled
 */
export async function takePhoto(): Promise<PhotoFile | null> {
  try {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // Use Capacitor Camera plugin on native platforms with optimized settings
      const image = await Camera.getPhoto({
        quality: 60, // Reduced from 90 for smaller file size
        allowEditing: false,
        resultType: CameraResultType.Uri, // Use URI instead of Base64 for better performance
        source: CameraSource.Camera,
        width: 1280, // Max width to reduce file size
        correctOrientation: true,
        saveToGallery: false,
      });

      if (!image.webPath) {
        return null;
      }

      // Convert URI to File
      const fileName = `photo_${Date.now()}.jpg`;
      const file = await fileFromUri(image.webPath, fileName);

      // Create data URL for preview
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return {
        file,
        dataUrl,
        id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
    } else {
      // Fallback for web: use file input
      return await selectPhotoFromLibrary();
    }
  } catch (error) {
    console.error('Error taking photo:', error);
    return null;
  }
}

/**
 * Selects a photo from the device library/gallery
 * @returns Promise<PhotoFile | null> - The selected photo or null if cancelled
 */
export async function selectPhotoFromLibrary(): Promise<PhotoFile | null> {
  try {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // Use Capacitor Camera plugin on native platforms with optimized settings
      const image = await Camera.getPhoto({
        quality: 60, // Reduced from 90 for smaller file size
        allowEditing: false,
        resultType: CameraResultType.Uri, // Use URI instead of Base64 for better performance
        source: CameraSource.Photos,
        width: 1280, // Max width to reduce file size
        correctOrientation: true,
      });

      if (!image.webPath) {
        return null;
      }

      // Convert URI to File
      const fileName = `photo_${Date.now()}.jpg`;
      const file = await fileFromUri(image.webPath, fileName);

      // Create data URL for preview
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return {
        file,
        dataUrl,
        id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
    } else {
      // For web, use file input with compression
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.style.display = 'none';
        
        input.onchange = async (e) => {
          const target = e.target as HTMLInputElement;
          const selectedFile = target.files?.[0];
          
          if (!selectedFile) {
            resolve(null);
            return;
          }

          try {
            // Compress if larger than 500KB
            const file = selectedFile.size > 500 * 1024
              ? await compressImageFile(selectedFile, { maxWidth: 1280, quality: 0.7 })
              : selectedFile;

            // Read file as data URL for preview
            const reader = new FileReader();
            reader.onload = (event) => {
              const dataUrl = event.target?.result as string;
              resolve({
                file,
                dataUrl,
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              });
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          } catch (error) {
            console.error('Error processing selected file:', error);
            resolve(null);
          }
        };
        
        input.oncancel = () => resolve(null);
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
      });
    }
  } catch (error) {
    console.error('Error selecting photo:', error);
    return null;
  }
}

/**
 * Selects multiple photos from the device library/gallery
 * @returns Promise<PhotoFile[]> - Array of selected photos (empty if cancelled)
 */
export async function selectPhotosFromLibrary(): Promise<PhotoFile[]> {
  try {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // On native platforms, Capacitor Camera doesn't support multiple selection
      // So we'll allow selecting one photo at a time, but return it as an array
      // The caller can call this multiple times to add more photos
      const photo = await selectPhotoFromLibrary();
      return photo ? [photo] : [];
    } else {
      // For web, use file input with multiple selection
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true; // Enable multiple selection
        input.style.display = 'none';
        
        input.onchange = async (e) => {
          const target = e.target as HTMLInputElement;
          const selectedFiles = target.files;
          
          if (!selectedFiles || selectedFiles.length === 0) {
            resolve([]);
            return;
          }

          try {
            // Process all selected files
            const baseTimestamp = Date.now();
            const photoPromises = Array.from(selectedFiles).map(async (selectedFile, index) => {
              // Compress if larger than 500KB
              const file = selectedFile.size > 500 * 1024
                ? await compressImageFile(selectedFile, { maxWidth: 1280, quality: 0.7 })
                : selectedFile;

              // Read file as data URL for preview
              return new Promise<PhotoFile>((resolveFile, rejectFile) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                  const dataUrl = event.target?.result as string;
                  resolveFile({
                    file,
                    dataUrl,
                    id: `photo_${baseTimestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`,
                  });
                };
                reader.onerror = () => rejectFile(new Error('Failed to read file'));
                reader.readAsDataURL(file);
              });
            });

            const photos = await Promise.all(photoPromises);
            resolve(photos);
          } catch (error) {
            console.error('Error processing selected files:', error);
            resolve([]);
          }
        };
        
        input.oncancel = () => resolve([]);
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
      });
    }
  } catch (error) {
    console.error('Error selecting photos:', error);
    return [];
  }
}

